import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'ramadan-custom-tags';
const MAX_CUSTOM_TAGS = 5;
const MAX_TAG_LENGTH = 15;

export const useCustomTags = () => {
  const [customTags, setCustomTags] = useState<string[]>([]);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        setCustomTags(JSON.parse(saved));
      } catch {
        setCustomTags([]);
      }
    }
  }, []);

  const addCustomTag = useCallback((name: string): { success: boolean; error?: string } => {
    const trimmed = name.trim();
    if (!trimmed) return { success: false, error: 'Tag name cannot be empty' };
    if (trimmed.length > MAX_TAG_LENGTH) return { success: false, error: `Maximum ${MAX_TAG_LENGTH} characters` };
    if (!/^[a-zA-Z0-9\s]+$/.test(trimmed)) return { success: false, error: 'No special characters allowed' };
    
    const value = trimmed.toLowerCase();
    const allPresets = ['gratitude', 'reflection', 'dua', 'intention', 'lesson', 'outpouring'];
    if (allPresets.includes(value) || customTags.some(t => t.toLowerCase() === value)) {
      return { success: false, error: 'Tag already exists' };
    }
    if (customTags.length >= MAX_CUSTOM_TAGS) {
      return { success: false, error: `Maximum ${MAX_CUSTOM_TAGS} custom tags reached` };
    }

    const updated = [...customTags, trimmed];
    setCustomTags(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    return { success: true };
  }, [customTags]);

  const removeCustomTag = useCallback((name: string) => {
    const updated = customTags.filter(t => t !== name);
    setCustomTags(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  }, [customTags]);

  return {
    customTags,
    addCustomTag,
    removeCustomTag,
    canAddMore: customTags.length < MAX_CUSTOM_TAGS,
    maxReached: customTags.length >= MAX_CUSTOM_TAGS,
  };
};
